import { Holding } from "../../../../services/holdings";
import { localToday } from "../../../../utils";
import { holdingOpenedText } from "../../utils";
import { centsOf, settlementNetCentsOf } from "../settlementNote";
import {
  HoldingOption,
  HoldingTradeAction,
  HoldingTradeCopy,
  HoldingTradeFormValues,
} from "./types";

export const FORM_ID = "holding-trade";

export const ACTION_SEGMENTS: Array<{
  value: HoldingTradeAction;
  label: string;
}> = [
  { value: "sell", label: "Sell" },
  { value: "dividend", label: "Dividend" },
  { value: "fee", label: "Fee" },
  { value: "tax", label: "Tax" },
];

// Module constant, not a HOLDING_TRADE_COPY entry: the fee field is sell-only but still shared between desktop and compact.
export const FEE_HELPER =
  "Optional — the broker's commission, deducted from the amount above.";

/** The dividend's tax helper, a module constant for the same reason. */
export const WITHHOLDING_TAX_HELPER =
  "Optional — tax withheld at source, deducted from the amount above.";

// One keyed map for every mode — keeping the wording together stops the modes drifting apart.
export const HOLDING_TRADE_COPY: Record<HoldingTradeAction, HoldingTradeCopy> =
  {
    sell: {
      eyebrow: "SELL POSITION",
      title: (investmentName) => `Sell from ${investmentName}`,
      ariaLabel: "Sell holding form",
      submitLabel: "Record sale",
      // "Save", not "Sell" — the segmented control already has a Sell button, and two same-named buttons are ambiguous when navigating by name.
      compactActionLabel: "Save",
      compactTitle: "Sell holding",
      // The amount is the gross proceeds, and the fee below is deducted from it —
      // saying so is what stops the commission being netted off here as well.
      amountHelper:
        "What the sale raised, before the broker's commission. Enter that commission as the fee below.",
      positionHelper: "Only positions you still hold can be sold.",
      discardTitle: "Cancel this sale?",
    },
    dividend: {
      eyebrow: "RECORD DIVIDEND",
      title: (investmentName) => `Record a dividend in ${investmentName}`,
      ariaLabel: "Record dividend form",
      submitLabel: "Record dividend",
      compactActionLabel: "Save",
      compactTitle: "Record dividend",
      // The gross payment, with the withheld tax entered below and deducted
      // from it — saying so is what stops the tax being netted off here as well.
      amountHelper:
        "The dividend declared, before any tax withheld at source. Enter that tax below.",
      positionHelper:
        "Closed positions stay available — income and charges can land after a sale.",
      discardTitle: "Cancel this dividend?",
    },
    fee: {
      eyebrow: "RECORD FEE",
      title: (investmentName) => `Record a fee in ${investmentName}`,
      ariaLabel: "Record fee form",
      submitLabel: "Record fee",
      compactActionLabel: "Save",
      compactTitle: "Record fee",
      amountHelper: "The commission or charge debited from the account.",
      positionHelper:
        "Closed positions stay available — income and charges can land after a sale.",
      discardTitle: "Cancel this fee?",
    },
    tax: {
      eyebrow: "RECORD WITHHOLDING TAX",
      title: (investmentName) => `Record withholding tax in ${investmentName}`,
      ariaLabel: "Record withholding tax form",
      submitLabel: "Record tax",
      compactActionLabel: "Save",
      compactTitle: "Record tax",
      // Distinct from the dividend's own tax field: this is tax the broker reports as its own line, not tax entered alongside a dividend.
      amountHelper:
        "Tax withheld at source, when your broker reports it separately from the gross dividend.",
      positionHelper:
        "Closed positions stay available — income and charges can land after a sale.",
      discardTitle: "Cancel this tax entry?",
    },
  };

const buildHoldingLabel = (holding: Holding): string => {
  const label = `${holding.instrument.symbol} · ${holdingOpenedText(holding.openedAt)}`;
  return holding.isClosed ? `${label} · closed` : label;
};

// Closed positions stay selectable for dividend/fee/tax (a since-sold position can still owe tax) but filtered for sell, where a zero position fails the replay.
export const buildHoldingOptions = (
  holdings: Holding[],
  action: HoldingTradeAction,
): HoldingOption[] =>
  holdings
    .filter((holding) => action !== "sell" || !holding.isClosed)
    .map((holding) => ({
      value: String(holding.id),
      label: buildHoldingLabel(holding),
      holdingId: holding.id,
      quantity: holding.quantity,
      isClosed: holding.isClosed,
      symbol: holding.instrument.symbol,
      openedAt: holding.openedAt,
    }));

/** A fresh date is required whenever the panel opens or resets. */
export const createDefaultHoldingTrade = (
  action: HoldingTradeAction,
  holding: HoldingOption | null,
): HoldingTradeFormValues => ({
  action,
  holding,
  quantity: "",
  amountEur: "",
  feeEur: "",
  taxWithheld: "",
  date: localToday(),
  note: "",
});

const TRADE_CHARGE_FIELDS: Partial<
  Record<HoldingTradeAction, "feeEur" | "taxWithheld">
> = { sell: "feeEur", dividend: "taxWithheld" };

export const tradeNetCentsOf = (
  values: Pick<
    HoldingTradeFormValues,
    "action" | "amountEur" | "feeEur" | "taxWithheld"
  >,
): number => {
  const chargeField = TRADE_CHARGE_FIELDS[values.action];
  return settlementNetCentsOf({
    amountCents: centsOf(values.amountEur),
    chargeCents: chargeField ? centsOf(values[chargeField]) : 0,
    direction: chargeField ? "IN" : "OUT",
  });
};
